import { Injectable, signal } from '@angular/core';

/**
 * Egységes dátum kezelési szolgáltatás
 * 
 * Egyesíti a DateCalculationService, DateUtilsService és DebugDateService 
 * funkcionalitásait egy központosított service-ben.
 * 
 * Főbb funkciók:
 * - Dátum formázás és parsing
 * - Hét számítások (ISO 8601)
 * - Munkanapos ellenőrzések
 * - Debug dátum funkciók
 * - Utility függvények
 */
@Injectable({
  providedIn: 'root'
})
export class DateService {
  // Debug funkciók signal-jai
  private debugDateSignal = signal<Date | null>(null);
  private isDebugModeSignal = signal<boolean>(false);

  // Signals for reactive access
  debugDate = this.debugDateSignal.asReadonly();
  isDebugMode = this.isDebugModeSignal.asReadonly();

  // Magyar hónapnevek
  private readonly HUNGARIAN_MONTHS = [
    'január', 'február', 'március', 'április', 'május', 'június',
    'július', 'augusztus', 'szeptember', 'október', 'november', 'december'
  ];

  constructor() {
    // Load debug settings from localStorage if available
    this.loadDebugSettings();
  }

  // =========================================================================
  // ALAPVETŐ DÁTUM FUNKCIÓK
  // =========================================================================

  /**
   * Get the current date - either debug date or actual current date
   */
  getCurrentDate(): Date {
    const debugDate = this.debugDateSignal();
    return debugDate || new Date();
  }

  /**
   * Formats a Date object to string in yyyy.mm.dd format
   */
  formatDateToString(date: Date | null): string {
    if (!date) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}.${month}.${day}`;
  }

  /**
   * Parses a date string in yyyy.mm.dd format to Date object
   */
  parseStringToDate(dateString: string): Date | null {
    if (!dateString || dateString.trim() === '') return null;
    
    // yyyy.mm.dd formátum ellenőrzése
    const dateRegex = /^(\d{4})\.(\d{2})\.(\d{2})$/;
    const match = dateString.match(dateRegex);
    
    if (!match) return null;
    
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1; // JavaScript Date hónapok 0-indexeltek
    const day = parseInt(match[3], 10);
    
    const date = new Date(year, month, day);
    
    // Ellenőrizzük, hogy a létrehozott dátum megegyezik-e a bevitt értékekkel
    if (date.getFullYear() === year && 
        date.getMonth() === month && 
        date.getDate() === day) {
      return date;
    }
    
    return null;
  }

  /**
   * Format date for display in Hungarian locale
   */
  formatDate(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('hu-HU');
  }

  /**
   * Format date with time for display in Hungarian locale
   */
  formatDateTime(date: string | Date): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString('hu-HU');
  }

  // =========================================================================
  // HÓNAP KEZELÉSI FUNKCIÓK
  // =========================================================================

  /**
   * Hónap név alapján szám meghatározása (1-12)
   */
  getMonthNumber(monthName: string): number {
    return this.HUNGARIAN_MONTHS.indexOf(monthName.toLowerCase()) + 1;
  }

  /**
   * Hónap név alapján index meghatározása (0-11, JavaScript Date objektumhoz)
   */
  getMonthIndex(monthName: string): number {
    return this.HUNGARIAN_MONTHS.indexOf(monthName.toLowerCase());
  }

  /**
   * Get current month info
   */
  getCurrentMonthInfo(): { month: number; year: number } {
    const currentDate = this.getCurrentDate();
    return {
      month: currentDate.getMonth() + 1, // JavaScript months are 0-based
      year: currentDate.getFullYear()
    };
  }

  // =========================================================================
  // MUNKANAPOS ÉS HÉTVÉGE ELLENŐRZÉSEK
  // =========================================================================

  /**
   * A nap munkanapo-e (hétfő-péntek)
   */
  isWeekday(dayIndex: number, year: number, monthIndex: number, daysInMonth: number): boolean {
    if (dayIndex < 0 || dayIndex >= daysInMonth) return false;
    const date = new Date(year, monthIndex, dayIndex + 1);
    const dayOfWeek = date.getDay();
    return dayOfWeek >= 1 && dayOfWeek <= 5; // 1=Hétfő, 5=Péntek
  }

  /**
   * A nap hétvége-e (szombat-vasárnap)
   */
  isWeekend(dayIndex: number, year: number, monthIndex: number): boolean {
    const dayOfWeek = new Date(year, monthIndex, dayIndex + 1).getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // 0 = vasárnap, 6 = szombat
  }

  /**
   * Van-e hétvége a megadott napok tartományban
   */
  isAnyDayInRangeWeekend(startIndex: number, endIndex: number, year: number, monthIndex: number): boolean {
    for (let i = startIndex; i <= endIndex; i++) {
      if (this.isWeekend(i, year, monthIndex)) {
        return true;
      }
    }
    return false;
  }

  // =========================================================================
  // HÉT SZÁMÍTÁSI FUNKCIÓK (ISO 8601)
  // =========================================================================

  /**
   * Dátumból hétvége kinyerése ISO 8601 standard szerint
   */
  getWeekNumber(date: Date): number {
    // ISO 8601 hétvége számítás (standard algoritmus)
    const tempDate = new Date(date.getTime());
    tempDate.setHours(0, 0, 0, 0);
    tempDate.setDate(tempDate.getDate() + 3 - (tempDate.getDay() + 6) % 7);
    const week1 = new Date(tempDate.getFullYear(), 0, 4);
    const weekNumber = Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7) + 1;
    
    return Math.max(1, Math.min(52, weekNumber));
  }

  /**
   * Get week info (number and year) for a given date
   */
  getWeekInfo(date: Date): { weekNumber: number; year: number } {
    return {
      weekNumber: this.getWeekNumber(date),
      year: date.getFullYear()
    };
  }

  /**
   * Get current week number and year based on debug or actual date
   */
  getCurrentWeekInfo(): { weekNumber: number; year: number } {
    const currentDate = this.getCurrentDate();
    return this.getWeekInfo(currentDate);
  }

  /**
   * Meghatározza hogy az adott hét adott napja melyik napi indexre esik a hónapban
   * (Kompatibilis a CallCenterStatsService algoritmusával)
   */
  getDayIndexForWeekDay(
    weekNumber: number, 
    weekDayIndex: number, 
    year: number,
    monthIndex: number,
    daysInMonth: number
  ): number {
    try {
      // ISO hét alapján keressük meg a hét hétfőjét
      // ISO week algoritmus: az első csütörtöktől számítjuk a heteket
      const jan4 = new Date(year, 0, 4);
      const week1Monday = new Date(jan4.getTime() - ((jan4.getDay() + 6) % 7) * 86400000);
      
      // A keresett hét hétfőjének dátuma
      const targetMonday = new Date(week1Monday.getTime() + (weekNumber - 1) * 7 * 86400000);
      
      // A keresett nap dátuma (hétfő + weekDayIndex)
      const targetDate = new Date(targetMonday.getTime() + weekDayIndex * 86400000);
      
      // Ellenőrizzük hogy a dátum az aktuális hónapba esik-e
      if (targetDate.getFullYear() === year && targetDate.getMonth() === monthIndex) {
        const dayOfMonth = targetDate.getDate();
        
        // Ellenőrizzük hogy a nap a hónap határain belül van-e
        if (dayOfMonth >= 1 && dayOfMonth <= daysInMonth) {
          return dayOfMonth - 1; // 0-indexelve
        }
      }
    } catch (error) {
      // Silent error handling
    }
    
    return -1; // Nem esik a hónapba vagy hiba történt
  }

  /**
   * Hét számok generálása a megadott évhez és hónaphoz
   */
  generateWeekNumbers(year: number, monthIndex: number, daysInMonth: number): number[] {
    const weekNumbers: number[] = [];
    const effectiveYear = year; // Lehet debug mód
    
    for (let i = 0; i < daysInMonth; i++) {
      const date = new Date(effectiveYear, monthIndex, i + 1);
      const weekNumber = this.getWeekNumber(date);
      weekNumbers.push(weekNumber);
    }
    
    return weekNumbers;
  }

  /**
   * Nap számok generálása (1, 2, 3, ...)
   */
  generateDayNumbers(daysInMonth: number): number[] {
    return Array.from({length: daysInMonth}, (_, i) => i + 1);
  }

  /**
   * Heti csoportok generálása megjelenítéshez
   */
  generateWeekGroups(
    weekNumbers: number[], 
    daysInMonth: number, 
    year: number, 
    monthIndex: number
  ): {weekNumber: number, startDay: number, colspan: number, isWeekend: boolean}[] {
    const groups: {weekNumber: number, startDay: number, colspan: number, isWeekend: boolean}[] = [];
    
    if (weekNumbers.length === 0) return groups;

    let currentWeek = weekNumbers[0];
    let startDay = 0;
    let count = 1;

    for (let i = 1; i < weekNumbers.length; i++) {
      if (weekNumbers[i] === currentWeek) {
        count++;
      } else {
        // Ellenőrizzük hogy van-e hétvége a csoportban
        const isWeekend = this.isAnyDayInRangeWeekend(startDay, startDay + count - 1, year, monthIndex);
        
        groups.push({
          weekNumber: currentWeek,
          startDay: startDay,
          colspan: count,
          isWeekend: isWeekend
        });

        currentWeek = weekNumbers[i];
        startDay = i;
        count = 1;
      }
    }

    // Utolsó csoport hozzáadása
    const isWeekend = this.isAnyDayInRangeWeekend(startDay, startDay + count - 1, year, monthIndex);
    groups.push({
      weekNumber: currentWeek,
      startDay: startDay,
      colspan: count,
      isWeekend: isWeekend
    });

    return groups;
  }

  // =========================================================================
  // UTILITY FUNKCIÓK
  // =========================================================================

  /**
   * Check if two dates are on the same day
   */
  isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  /**
   * Add days to a date
   */
  addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  /**
   * Subtract days from a date
   */
  subtractDays(date: Date, days: number): Date {
    return this.addDays(date, -days);
  }

  /**
   * Get start of day
   */
  getStartOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    return result;
  }

  /**
   * Get end of day
   */
  getEndOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(23, 59, 59, 999);
    return result;
  }

  /**
   * Creates an empty individual with default values
   */
  createEmptyIndividual() {
    return {
      fullName: '',
      birthDate: null,
      birthPlace: '',
      motherName: '',
      taxNumber: '',
      address: ''
    };
  }

  // =========================================================================
  // DEBUG FUNKCIÓK
  // =========================================================================

  /**
   * Set a debug date - when set, this date will be used instead of current date
   */
  setDebugDate(date: Date | null): void {
    this.debugDateSignal.set(date);
    this.isDebugModeSignal.set(date !== null);
    this.saveDebugSettings();
  }

  /**
   * Clear debug date and return to normal mode
   */
  clearDebugDate(): void {
    this.debugDateSignal.set(null);
    this.isDebugModeSignal.set(false);
    this.saveDebugSettings();
  }

  /**
   * Format debug date for display
   */
  formatDebugDate(date: Date): string {
    return date.toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Save debug settings to localStorage
   */
  private saveDebugSettings(): void {
    const debugDate = this.debugDateSignal();
    if (debugDate) {
      localStorage.setItem('crm_debug_date', debugDate.toISOString());
      localStorage.setItem('crm_debug_mode', 'true');
    } else {
      localStorage.removeItem('crm_debug_date');
      localStorage.setItem('crm_debug_mode', 'false');
    }
  }

  /**
   * Load debug settings from localStorage
   */
  private loadDebugSettings(): void {
    try {
      const debugModeStr = localStorage.getItem('crm_debug_mode');
      const debugDateStr = localStorage.getItem('crm_debug_date');
      
      if (debugModeStr === 'true' && debugDateStr) {
        const debugDate = new Date(debugDateStr);
        if (!isNaN(debugDate.getTime())) {
          this.debugDateSignal.set(debugDate);
          this.isDebugModeSignal.set(true);
        }
      }
    } catch (error) {
      console.warn('Failed to load debug settings:', error);
      this.clearDebugDate();
    }
  }

  // =========================================================================
  // KOMPLEX DÁTUM FELDOLGOZÁSI FUNKCIÓK
  // =========================================================================

  /**
   * Objektum date mezőinek konvertálása timezone-safe YYYY.MM.DD formátumra
   * @param obj Konvertálandó objektum
   * @param dateFields Date mezők nevei (pl: ['birthDate', 'startDate'])
   * @returns Konvertált objektum
   */
  processDateFields<T>(obj: T, dateFields: string[]): T {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    const processed = JSON.parse(JSON.stringify(obj)); // Deep copy
    const objAny = obj as any;
    const processedAny = processed as any;

    dateFields.forEach(field => {
      if (this.hasProperty(objAny, field) && objAny[field] instanceof Date) {
        processedAny[field] = this.formatDateToString(objAny[field]);
      }
    });

    return processed;
  }

  /**
   * Tömb objektumok date mezőinek konvertálása
   * @param array Objektumok tömbje
   * @param dateFields Date mezők nevei
   * @returns Konvertált tömb
   */
  processArrayDateFields<T>(array: T[], dateFields: string[]): T[] {
    if (!Array.isArray(array)) {
      return array;
    }

    return array.map(item => this.processDateFields(item, dateFields));
  }

  /**
   * Komplex objektum structure date mezőinek rekurzív konvertálása
   * @param obj Konvertálandó objektum
   * @param fieldMap Mezők map-ja: { 'mezőnév': ['dateMező1', 'dateMező2'] }
   * @returns Konvertált objektum
   */
  processNestedDateFields<T>(obj: T, fieldMap: { [key: string]: string[] }): T {
    if (!obj || typeof obj !== 'object') {
      return obj;
    }

    const processed = JSON.parse(JSON.stringify(obj)); // Deep copy

    Object.entries(fieldMap).forEach(([fieldPath, dateFields]) => {
      const fieldValue = this.getNestedProperty(obj, fieldPath);
      
      if (Array.isArray(fieldValue)) {
        this.setNestedProperty(processed, fieldPath, 
          this.processArrayDateFields(fieldValue, dateFields));
      } else if (fieldValue) {
        this.setNestedProperty(processed, fieldPath, 
          this.processDateFields(fieldValue, dateFields));
      }
    });

    return processed;
  }

  private hasProperty(obj: any, prop: string): boolean {
    return obj != null && typeof obj === 'object' && prop in obj;
  }

  private getNestedProperty(obj: any, path: string): any {
    return path.split('.').reduce((current, prop) => current?.[prop], obj);
  }

  private setNestedProperty(obj: any, path: string, value: any): void {
    const props = path.split('.');
    const lastProp = props.pop()!;
    const target = props.reduce((current, prop) => current[prop], obj);
    target[lastProp] = value;
  }
}